import React, { useState, useEffect, useRef } from 'react';
import { youtubeAdminAPI } from '../services/api';

const RapperInput = ({ value, existingId, onChange, onSelect, placeholder, index, onRemove, canRemove }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Search for artists as user types
  useEffect(() => {
    const searchArtists = async () => {
      if (value && value.length >= 2 && !existingId) {
        setLoading(true);
        try {
          const response = await youtubeAdminAPI.searchArtists(value);
          setSuggestions(response.data.artists || []);
          setShowDropdown(true);
        } catch (error) {
          console.error('Error searching artists:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    };

    const debounce = setTimeout(searchArtists, 300);
    return () => clearTimeout(debounce);
  }, [value, existingId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside this specific dropdown AND not clicking on another dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.rapper-input-container')
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleSelectExisting = (artist) => {
    onSelect(artist.id, artist.display_name || artist.username);
    setShowDropdown(false);
  };

  const handleUseAsNew = () => {
    onSelect(null, value);
    setShowDropdown(false);
  };

  return (
    <div className="relative rapper-input-container" ref={dropdownRef}>
      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={existingId ? value : value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full px-3 py-2 border rounded text-white text-sm focus:outline-none ${
              existingId
                ? 'bg-green-900 border-green-600 focus:border-green-500'
                : 'bg-battle-dark border-army/40 focus:border-brand-yellow/60'
            }`}
            disabled={!!existingId}
          />
          {existingId && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
              <span className="text-xs text-green-400">✓ Existing</span>
              <button
                onClick={() => onSelect(null, '')}
                className="text-gray-400 hover:text-white"
                title="Clear selection"
              >
                ✕
              </button>
            </div>
          )}
        </div>
        {canRemove && (
          <button
            onClick={onRemove}
            className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
            title="Remove rapper"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && !existingId && (
        <div className="z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: inputRef.current?.getBoundingClientRect().bottom + window.scrollY,
            left: inputRef.current?.getBoundingClientRect().left + window.scrollX,
            width: inputRef.current?.offsetWidth
          }}>
          {loading ? (
            <div className="px-4 py-3 text-gray-400 text-sm">Searching...</div>
          ) : suggestions.length > 0 ? (
            <>
              <div className="px-3 py-2 bg-gray-700 text-xs text-gray-400 font-semibold">
                Existing Artists
              </div>
              {suggestions.map((artist) => (
                <button
                  key={artist.id}
                  onClick={() => handleSelectExisting(artist)}
                  className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center justify-between group"
                >
                  <span>{artist.display_name}</span>
                  <span className="text-xs text-gray-400 group-hover:text-green-400">
                    Select
                  </span>
                </button>
              ))}
              <div className="border-t border-gray-700"></div>
            </>
          ) : null}
          <button
            onClick={handleUseAsNew}
            className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 flex items-center justify-between group"
          >
            <div>
              <div className="font-medium">Create new: "{value}"</div>
              <div className="text-xs text-gray-400">Artist doesn't exist yet</div>
            </div>
            <span className="text-xs text-gray-400 group-hover:text-blue-400">
              Create
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default RapperInput;
